// Fichier : Table.tsx
// Ce fichier définit le composant Table qui utilise MantineReactTable pour afficher une table de utilisateurs avec différentes actions comme la vue, l'édition, la suppression, et la gestion des Tabs utilisateur.

import { useEffect, useMemo } from "react";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from "mantine-react-table";
import { User } from "../../types/generale";
import { useNavigate } from "react-router-dom";
import { useDeleteUserMutation } from "@/state/usersApiSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";

export default function Table({ users }: { users: User[] }) {
  const [deleteUser, { isError }] = useDeleteUserMutation(); // Mutation pour supprimer un utilisateur
  const navigate = useNavigate();

  // Fonction pour gérer le changement de sélection dans le menu déroulant des actions
  function handleSelectChange(selectedValue: any, row: any) {
    switch (selectedValue) {
      case "view":
        navigate(`/users/${row.original.id}/view`);
        break;
      case "delete":
        deleteUser(row.original.id);
        if (!isError) notifyDelete();
        break;
      case "viewTabs":
        navigate(`/users/${row.original.id}/tabs/edit`);
        break;
      default:
        break;
    }
  }

  const notifyDelete = () =>
    toast.success("User Deleted Successfully", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  // Notification d'erreur si la suppression de l'utilisateur échoue en raison d'onglets actifs
  const notifyError = () =>
    toast.error("You Can't delete a User who have active Tabs", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  // Affichage de la notification d'erreur si isError est vrai
  useEffect(() => {
    if (isError) notifyError();
  }, [isError]);

  // Définition des colonnes de la table à l'aide de useMemo
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
      },
      {
        accessorKey: "username",
        header: "Username",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
      },
      {
        accessorKey: "tenant_id",
        header: "Tenant_id",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
      },
      {
        accessorKey: "wordpress_user",
        header: "Wordpress",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
      },
      {
        accessorKey: "chatwoot_user",
        header: "chatwoot",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "center",
        },
      },

      {
        accessorKey: "actions",
        header: "Actions",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false, // Désactivation du tri pour cette colonne
        Cell: (
          { row } // Composant Cell personnalisé pour afficher le menu déroulant des actions , il contient un select avec des options qui seront trackés par le switch au dessus.
        ) => (
          <div className="relative flex items-center">
            <FontAwesomeIcon
              icon={faEllipsisV}
              className="absolute left-10 pointer-events-none"
              size={"lg"}
            />
            <select
              className="pr-6 appearance-none border-none text-center font-bold  py-2 rounded leading-tight outline-none focus:border-blue-500"
              onChange={(e) => handleSelectChange(e.target.value, row)}
            >
              <option value=""></option>
              <option value="view">Details</option>
              <option value="viewTabs">Tabs</option>
              <option value="delete">Delete</option>
            </select>
          </div>
        ),
      },
    ],
    []
  );

  // Utilisation de useMantineReactTable pour configurer la table avec les colonnes et les données
  const table = useMantineReactTable({
    columns,
    data: users,
    enableColumnOrdering: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableHiding: false,
    enableSorting: true,
    enableColumnPinning: false,
    defaultColumn: {
      minSize: 1,
      maxSize: 3,
      size: 2,
    },
    enableFacetedValues: true,
    enableRowActions: false,
    enableGrouping: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: "xs",
    },
    mantineTableBodyCellProps: {
      sx: {
        textAlign: "center",
        fontSize: "11px !important",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontSize: "11px !important",
        fontWeight: "bold",
      },
    },
    enablePagination: false,
    enableRowSelection: false,
    enableGlobalFilter: false,
  });

  return <MantineReactTable table={table} />; // Rendu de la table à l'aide de MantineReactTable
}
